import React, { useCallback, useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Bell, BellOff, BellRing, Loader2, Sparkles } from 'lucide-react';
import {
  enablePushNotifications,
  getPushNotificationStatus,
  isPushSupported,
} from '../utils/pushNotifications';

const NotificationSettingsCard = ({ userId }) => {
  const [hidden, setHidden] = useState(false);
  const [status, setStatus] = useState({
    supported: true,
    enabled: false,
    permission: 'default',
    loading: true,
  });
  const [activating, setActivating] = useState(false);
  const [feedback, setFeedback] = useState(null);

  const refreshStatus = useCallback(async () => {
    try {
      const next = await getPushNotificationStatus({ syncNative: Boolean(userId) });
      setStatus({ ...next, loading: false });

      if (next.enabled) {
        setHidden(true);
      }
    } catch {
      setStatus((prev) => ({ ...prev, loading: false }));
    }
  }, [userId]);

  useEffect(() => {
    let mounted = true;
    let permissionHandler;

    const setup = async () => {
      await refreshStatus();
      if (!mounted) return;

      if (window.OneSignal?.Notifications?.addEventListener) {
        permissionHandler = () => refreshStatus();
        window.OneSignal.Notifications.addEventListener('permissionChange', permissionHandler);
      }
    };

    setup();
    window.addEventListener('focus', refreshStatus);

    return () => {
      mounted = false;
      window.removeEventListener('focus', refreshStatus);
      if (permissionHandler && window.OneSignal?.Notifications?.removeEventListener) {
        window.OneSignal.Notifications.removeEventListener('permissionChange', permissionHandler);
      }
    };
  }, [refreshStatus]);

  const handleActivate = async () => {
    if (!userId || activating) return;

    setActivating(true);
    setFeedback(null);

    try {
      const next = await enablePushNotifications(userId);
      setStatus({ ...next, loading: false });

      if (next.enabled) {
        setHidden(true);
      } else {
        setFeedback({
          type: 'error',
          text: 'El permiso está activo pero no se completó el registro. Pulsa de nuevo o recarga la página.',
        });
      }
    } catch (error) {
      console.error('Error activando notificaciones:', error);

      if (error?.code === 'PERMISSION_DENIED' || error?.message === 'PERMISSION_DENIED') {
        setFeedback({
          type: 'error',
          text: 'Permiso denegado. Habilítalo en ajustes del navegador para este sitio.',
        });
        await refreshStatus();
      } else {
        setFeedback({
          type: 'error',
          text: error?.message || 'No se pudieron activar las notificaciones. Intenta de nuevo.',
        });
      }
    } finally {
      setActivating(false);
    }
  };

  const isBlocked = status.permission === 'denied';
  const supported = isPushSupported();

  if (hidden || (!status.loading && status.enabled)) {
    return null;
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0, y: 16 }}
        animate={{ opacity: 1, y: 0 }}
        exit={{ opacity: 0, y: -12, height: 0, marginBottom: 0 }}
        transition={{ duration: 0.35 }}
        className="relative mb-8 overflow-hidden rounded-[32px] border border-blue-100 bg-white shadow-sm"
      >
        <div className="absolute inset-0 bg-gradient-to-br from-blue-600 via-blue-700 to-blue-950" />
        <div className="absolute -right-10 -top-10 h-40 w-40 rounded-full bg-white/10 blur-2xl" />
        <div className="absolute -bottom-12 -left-8 h-36 w-36 rounded-full bg-cyan-400/20 blur-2xl" />

        <div className="relative z-10 p-6 md:p-7">
          <div className="flex items-start gap-4">
            <div className="relative shrink-0">
              <div className="flex h-16 w-16 items-center justify-center rounded-[22px] border border-white/20 bg-white/15 shadow-lg shadow-blue-950/20 backdrop-blur-md">
                {status.loading ? (
                  <Loader2 className="h-7 w-7 animate-spin text-white/80" />
                ) : isBlocked ? (
                  <BellOff className="h-7 w-7 text-amber-200" />
                ) : (
                  <Bell className="h-7 w-7 text-white" />
                )}
              </div>
            </div>

            <div className="min-w-0 flex-1 pt-1">
              <div className="mb-1 flex items-center gap-2">
                <Sparkles className="h-3.5 w-3.5 text-cyan-200" />
                <span className="text-[10px] font-black uppercase tracking-[0.2em] text-blue-100">
                  Alertas en tiempo real
                </span>
              </div>
              <h3 className="text-lg font-black tracking-tight text-white">
                Notificaciones Push
              </h3>
              <p className="mt-1.5 text-[13px] font-medium leading-relaxed text-blue-100/90">
                {isBlocked
                  ? 'Las notificaciones están bloqueadas en tu navegador. Habilítalas en ajustes del sitio.'
                  : status.permission === 'granted' && !status.enabled
                    ? 'Ya diste permiso, pero falta registrar tu dispositivo en el servicio de alertas. Pulsa el botón para completar el registro.'
                    : 'Pulsa el botón y acepta en el diálogo del navegador. Te enviaremos un aviso de confirmación.'}
              </p>
            </div>
          </div>

          <div className="mt-6">
            {!supported ? (
              <div className="rounded-2xl border border-white/15 bg-black/20 px-4 py-3 text-center text-[12px] font-bold text-blue-100">
                Tu dispositivo no admite notificaciones web.
              </div>
            ) : isBlocked ? (
              <div className="rounded-2xl border border-amber-300/30 bg-amber-400/10 px-4 py-3.5 text-[12px] font-bold leading-relaxed text-amber-100">
                Ve a configuración del navegador → Permisos → Notificaciones → Permitir para este sitio.
              </div>
            ) : (
              <>
                <motion.button
                  whileTap={{ scale: 0.98 }}
                  onClick={handleActivate}
                  disabled={activating || status.loading}
                  className="group relative flex w-full items-center justify-center gap-2.5 overflow-hidden rounded-2xl bg-white px-5 py-4 text-sm font-black uppercase tracking-widest text-blue-900 shadow-lg shadow-blue-950/30 transition hover:bg-blue-50 disabled:cursor-not-allowed disabled:opacity-70"
                >
                  <span className="absolute inset-0 -translate-x-full bg-gradient-to-r from-transparent via-blue-100/60 to-transparent transition-transform duration-700 group-hover:translate-x-full" />
                  {activating ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Registrando dispositivo...
                    </>
                  ) : (
                    <>
                      <BellRing className="h-4 w-4" />
                      Activar notificaciones
                    </>
                  )}
                </motion.button>

                {feedback?.type === 'error' && (
                  <p className="mt-3 rounded-xl border border-red-300/30 bg-red-500/10 px-3 py-2 text-center text-[11px] font-bold text-red-100">
                    {feedback.text}
                  </p>
                )}
              </>
            )}
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  );
};

export default NotificationSettingsCard;
